import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { AccessControlGroupResponse, AccessControlGroupType } from "api/ApiClient";
import { BASE_API_URL } from "store/Me";
import { AdminTable } from "components/shared/AdminTable";

const ACG_TYPE_LABEL: Record<string, string> = {
  [AccessControlGroupType.Regular]: "Regular",
  [AccessControlGroupType.DefaultForProperty]: "Default",
  [AccessControlGroupType.CommonDoors]: "Common Doors",
  [AccessControlGroupType.Master]: "Master",
  [AccessControlGroupType.Unit]: "Unit",
  [AccessControlGroupType.DefaultForGuests]: "Guest Default",
  [AccessControlGroupType.DefaultForStaff]: "Staff Default",
  [AccessControlGroupType.StaffOnly]: "Staff Only",
};

async function getAcgsForSite(siteId: string): Promise<AccessControlGroupResponse[]> {
  const params = new URLSearchParams();
  params.append("siteId", siteId);
  params.append("pageSize", "500");
  const res = await axios.get<AccessControlGroupResponse[]>(
    `${BASE_API_URL}/api/AccessControlGroups?${params.toString()}`,
  );
  return res.data ?? [];
}

export default function SiteAccessControlGroups({ siteId }: { siteId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["site-acgs", siteId],
    queryFn: () => getAcgsForSite(siteId),
    enabled: !!siteId,
  });

  const acgs = React.useMemo(
    () => [...(data ?? [])].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [data],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
        Loading access control groups...
      </div>
    );
  }

  return (
    <AdminTable aria-label="Site access control groups">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">External ID</th>
            <th scope="col">Type</th>
            <th scope="col">Property</th>
            <th scope="col">Locks</th>
          </tr>
        </thead>
        <tbody>
          {acgs.map((acg) => (
            <tr key={acg.id}>
              <td>
                <Link to={`/admin/accessControlGroups/${acg.id}`}>
                  {acg.name ?? "—"}
                </Link>
              </td>
              <td>{acg.externalId ?? "—"}</td>
              <td>
                {acg.type ? (ACG_TYPE_LABEL[acg.type as string] ?? acg.type) : "—"}
              </td>
              <td>{acg.propertyExternalId ?? "—"}</td>
              <td>{acg.locks?.length ?? 0}</td>
            </tr>
          ))}
          {acgs.length === 0 && (
            <tr>
              <td colSpan={5} className="py-16 text-center text-sm text-slate-500">
                No access control groups found for this site.
              </td>
            </tr>
          )}
        </tbody>
    </AdminTable>
  );
}

